import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { parseApiError, useAuth, usePermission } from '@saas-kit/lib';
import { toast } from '@saas-kit/ui';

import {
  callNext,
  claimCounter,
  completeToken,
  getMySession,
  getWorkstation,
  listClaimableCounters,
  listServices,
  noShowToken,
  recallToken,
  releaseCounter,
  resetDay,
  sendTokenToCounter,
  serveToken,
  transferToken,
} from './api';

const POLL_MS = 4000;

/** All queries and mutations behind the counter workstation page. */
export function useWorkstation() {
  const { t } = useTranslation('qms');
  const tenantId = useAuth((s) => s.selectedTenant?.tenant_id);
  const canView = usePermission('qms.tokens.view');
  const canClaim = usePermission('qms.counters.claim');
  const canResetDay = usePermission('qms.day.reset');
  const queryClient = useQueryClient();

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['qms-my-session'] }),
      queryClient.invalidateQueries({ queryKey: ['qms-workstation'] }),
    ]);
  const fail = (e: unknown) => toast.error(parseApiError(e).message);
  const done = (key: string) => async (token: { display_code: string }) => {
    await refresh();
    toast.success(t(key, { code: token.display_code }));
  };

  const sessionQuery = useQuery({
    queryKey: ['qms-my-session', tenantId],
    queryFn: getMySession,
    enabled: Boolean(tenantId && canView),
  });
  const session = sessionQuery.data ?? null;

  const workstationQuery = useQuery({
    queryKey: ['qms-workstation', tenantId, session?.counter_id],
    queryFn: () => getWorkstation(session!.counter_id),
    enabled: Boolean(tenantId && canView && session?.counter_id),
    refetchInterval: POLL_MS,
  });
  const workstation = workstationQuery.data ?? null;

  const servicesQuery = useQuery({
    queryKey: ['qms-services', tenantId],
    queryFn: listServices,
    enabled: Boolean(tenantId && canClaim),
  });
  const countersQuery = useQuery({
    queryKey: ['qms-claimable-counters', tenantId],
    queryFn: listClaimableCounters,
    enabled: Boolean(tenantId && canClaim),
    // Poll while picking so a counter another staff member just claimed
    // turns disabled without a manual refresh.
    refetchInterval: session ? false : POLL_MS,
  });

  const claim = useMutation({
    mutationFn: claimCounter,
    onSuccess: async () => {
      await refresh();
      toast.success(t('workstation.toasts.claimed'));
    },
    onError: fail,
  });
  const release = useMutation({
    mutationFn: releaseCounter,
    onSuccess: async () => {
      await refresh();
      toast.success(t('workstation.toasts.released'));
    },
    onError: fail,
  });
  const next = useMutation({
    mutationFn: callNext,
    onSuccess: async (token) => {
      await refresh();
      toast.success(
        token
          ? t('workstation.toasts.called', { code: token.display_code })
          : t('workstation.counter.queue_empty'),
      );
    },
    onError: fail,
  });
  const serve = useMutation({
    mutationFn: serveToken,
    onSuccess: refresh,
    onError: fail,
  });
  const complete = useMutation({
    mutationFn: completeToken,
    onSuccess: refresh,
    onError: fail,
  });
  const recall = useMutation({
    mutationFn: recallToken,
    onSuccess: done('workstation.toasts.recalled'),
    onError: fail,
  });
  const noShow = useMutation({
    mutationFn: noShowToken,
    onSuccess: done('workstation.toasts.no_show'),
    onError: fail,
  });
  const transfer = useMutation({
    mutationFn: (input: { tokenId: string; serviceId: string }) =>
      transferToken(input.tokenId, input.serviceId),
    onSuccess: done('workstation.toasts.transferred'),
    onError: fail,
  });
  const sendToCounter = useMutation({
    mutationFn: (input: { tokenId: string; counterId: string }) =>
      sendTokenToCounter(input.tokenId, input.counterId),
    onSuccess: done('workstation.toasts.sent'),
    onError: fail,
  });
  const reset = useMutation({
    mutationFn: resetDay,
    onSuccess: async (result) => {
      await Promise.all([
        refresh(),
        queryClient.invalidateQueries({
          queryKey: ['qms-claimable-counters'],
        }),
      ]);
      toast.success(
        t('workstation.toasts.day_reset', {
          tokens: result.cancelled_tokens,
          sessions: result.ended_sessions,
        }),
      );
    },
    onError: fail,
  });

  return {
    canView,
    canClaim,
    canResetDay,
    session,
    workstation,
    current: workstation?.current_token ?? null,
    services: (servicesQuery.data ?? []).filter((s) => s.is_active),
    counters: (countersQuery.data ?? []).filter((c) => c.is_active),
    claim,
    release,
    next,
    serve,
    complete,
    recall,
    noShow,
    transfer,
    sendToCounter,
    reset,
  };
}

export type WorkstationState = ReturnType<typeof useWorkstation>;
